import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [parts, setParts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalIsApprox, setTotalIsApprox] = useState(false);
  const [inventoryActive, setInventoryActive] = useState(false); // the user's shop has a price and stock list
  const [loading, setLoading] = useState(true); // true until the first search returns, so the empty state doesn't flash
  
  // Master lists
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterTypes, setMasterTypes] = useState([]);
  
  // Dynamic lists (filtered by search results)
  const [dynamicBrands, setDynamicBrands] = useState([]);
  const [dynamicTypes, setDynamicTypes] = useState([]);
  
  const [nlaTickets, setNlaTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('all'); // all | part | model
  const [filters, setFilters] = useState({
    status: 'All',
    applianceType: 'All',
    brand: 'All'
  });

  const lastBilledQuery = useRef('');
  const lastLoggedSearch = useRef(''); // `${mode}|${query}` already sent for miss logging
  const PAGE_SIZE = 50;

  const fetchInitialData = async () => {
    try {
      const { brands, types } = await api('lookups');
      setMasterBrands(brands || []);
      setMasterTypes(types || []);
    } catch (err) {
      console.error('Lookup load error:', err.message);
    }
  };

  const fetchTickets = useCallback(async () => {
    if (!user) return;
    try {
      const { tickets } = await api('listTickets');
      setNlaTickets(tickets || []);
    } catch (err) {
      console.error('Ticket load error:', err.message);
    }
  }, [user]);

  const searchParts = useCallback(async () => {
    if (!user || !profile) return;

    const trimmedQuery = searchQuery.trim();
    const isFree = profile.subscription_status === 'free';
    const limitReached = isFree && profile.free_searches_used >= profile.free_searches_limit;

    if (limitReached && trimmedQuery.length > 0) {
      setParts([]);
      setTotalCount(0);
      setTotalIsApprox(false);
      return;
    }

    setLoading(true);

    try {
      // The server enforces the free-search quota and does the billing.
      const billable = isFree && trimmedQuery.length > 0 && trimmedQuery !== lastBilledQuery.current;
      // A search reruns when filters or the profile change; only the first run of a query may log a miss.
      const searchKey = `${searchMode}|${trimmedQuery}`;
      const logMiss = trimmedQuery.length > 0 && searchKey !== lastLoggedSearch.current;
      const result = await api('searchParts', { query: trimmedQuery, filters, billable, mode: searchMode, logMiss });
      lastLoggedSearch.current = trimmedQuery ? searchKey : '';

      if (trimmedQuery) {
        if (billable) {
          lastBilledQuery.current = trimmedQuery;
          await refreshProfile();
        }
      } else {
        lastBilledQuery.current = '';
      }

      setTotalCount(result.totalCount || 0);
      setInventoryActive(Boolean(result.inventoryActive));
      setTotalIsApprox(Boolean(result.hasMore));
      setParts(result.parts ? Array.from(new Map(result.parts.map(p => [p.id, p])).values()) : []);

      // Brand/type filter options narrow to what the current search actually matched
      if (trimmedQuery) {
        setDynamicBrands(masterBrands.filter(b => result.brands.includes(b.raw_value)));
        setDynamicTypes(masterTypes.filter(t => result.types.includes(t.code)));
      } else {
        setDynamicBrands(masterBrands);
        setDynamicTypes(masterTypes);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchMode, filters, user, profile, refreshProfile, masterBrands, masterTypes]);

  useEffect(() => {
    if (user) {
      fetchInitialData();
      fetchTickets();
    }
  }, [user, fetchTickets]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (user && profile) searchParts();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, searchMode, filters, user, profile, searchParts]);

  return (
    <AppContext.Provider value={{
      parts,
      totalCount,
      totalIsApprox,
      inventoryActive,
      loading,
      brandOptions: [{ label: 'All Brands', value: 'All' }, ...dynamicBrands.map(b => ({ label: b.display_name, value: b.raw_value }))],
      typeOptions: [{ label: 'All Types', value: 'All' }, ...dynamicTypes.map(t => ({ label: t.display_name, value: t.code }))],
      nlaTickets,
      searchQuery,
      setSearchQuery,
      searchMode,
      setSearchMode,
      filters,
      setFilters,
      pendingNLACount: nlaTickets.filter(t => t.status === 'Pending').length,
      refreshData: () => { searchParts(); fetchTickets(); }
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);